import type { PieceChar } from '@chessmate/protocol';
import { colorOf, indexToSquare, parsePlacement, type Placement, squareToIndex } from './coords.js';

export interface AttackTable {
  readonly white: Uint8Array;
  readonly black: Uint8Array;
}

/** Material value in pawn units (king = 0). Keyed by lowercase piece char. */
export const PIECE_VALUES: Record<string, number> = {
  p: 1,
  n: 3,
  b: 3,
  r: 5,
  q: 9,
  k: 0,
};

/** Piece material value in pawn units. */
export function pieceValue(char: PieceChar): number {
  const v = PIECE_VALUES[char.toLowerCase()];
  return v ?? 0;
}

const KNIGHT_OFFSETS = [
  [-2, -1], [-2, 1], [-1, -2], [-1, 2],
  [1, -2], [1, 2], [2, -1], [2, 1],
] as const;

const KING_OFFSETS = [
  [-1, -1], [-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0], [1, 1],
] as const;

const ROOK_DIRS = [[-1, 0], [1, 0], [0, -1], [0, 1]] as const;
const BISHOP_DIRS = [[-1, -1], [-1, 1], [1, -1], [1, 1]] as const;

function inBounds(r: number, c: number): boolean {
  return r >= 0 && r < 8 && c >= 0 && c < 8;
}

/** Pseudo-legal squares attacked by the piece at index `i` (does not account for pins/checks). */
export function squaresAttacked(placement: Placement, i: number): number[] {
  const char = placement[i];
  if (char == null) return [];
  const color = colorOf(char);
  const type = char.toLowerCase();
  const r = Math.floor(i / 8);
  const c = i % 8;
  const out: number[] = [];
  const push = (rr: number, cc: number) => {
    if (inBounds(rr, cc)) out.push(rr * 8 + cc);
  };

  if (type === 'p') {
    // White pawns attack one rank toward black (decreasing rank index); black toward rank 1.
    const dir = color === 'w' ? -1 : 1;
    push(r + dir, c - 1);
    push(r + dir, c + 1);
    return out;
  }
  if (type === 'n') {
    for (const [dr, dc] of KNIGHT_OFFSETS) push(r + dr, c + dc);
    return out;
  }
  if (type === 'k') {
    for (const [dr, dc] of KING_OFFSETS) push(r + dr, c + dc);
    return out;
  }
  const dirs = type === 'r' ? ROOK_DIRS : type === 'b' ? BISHOP_DIRS : [...ROOK_DIRS, ...BISHOP_DIRS];
  for (const [dr, dc] of dirs) {
    let rr = r + dr;
    let cc = c + dc;
    while (inBounds(rr, cc)) {
      push(rr, cc);
      if (placement[rr * 8 + cc] != null) break; // blocked
      rr += dr;
      cc += dc;
    }
  }
  return out;
}

/** True if any piece of `attacker` color pseudo-legally attacks square index `target`. */
export function squareIsAttackedBy(placement: Placement, attacker: 'w' | 'b', target: number): boolean {
  for (let i = 0; i < 64; i++) {
    const char = placement[i];
    if (char == null) continue;
    if (colorOf(char) !== attacker) continue;
    if (squaresAttacked(placement, i).includes(target)) return true;
  }
  return false;
}

/** Bitmask of squares each color attacks. */
export function buildAttackTable(placement: Placement): AttackTable {
  const table: AttackTable = { white: new Uint8Array(64), black: new Uint8Array(64) };
  for (let i = 0; i < 64; i++) {
    const char = placement[i];
    if (char == null) continue;
    const side = colorOf(char);
    const target = squaresAttacked(placement, i);
    for (const t of target) {
      if (side === 'w') table.white[t] = 1;
      else table.black[t] = 1;
    }
  }
  return table;
}

/** Piece-weighted control score at a square (positive = white, negative = black). */
export function controlScoreAt(placement: Placement, target: number): number {
  let score = 0;
  for (let i = 0; i < 64; i++) {
    const char = placement[i];
    if (char == null) continue;
    if (!squaresAttacked(placement, i).includes(target)) continue;
    const side = colorOf(char);
    score += side === 'w' ? pieceValue(char) : -pieceValue(char);
  }
  return score;
}

export function controlScoreForFen(fen: string, sq: string): number {
  return controlScoreAt(parsePlacement(fen), squareToIndex(sq));
}

export { indexToSquare, parsePlacement, squareToIndex };
